import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE,
    OPEN, APPROVED, VERIFICATION,
    WORKFLOW_SEND_TO_LEVEL,
    WORKFLOW_RETURN_TO_LEVEL,
    WORKFLOW_APPROVED,
} from "const"
import {
    CcComplianceHistoryCreate,
    CcComplianceHistoryVersion,
    CcComplianceItemState,
    CcComplianceTempAnswer,
    CcComplianceTempAnswerVersion,
    MtDriveFile,
    MtDriveVersion,
} from "server/models"
import { StateType, CcStateSettingsType, useInput, replaceHistoryState } from "client/utils"
import {
    StoreType,
    ComplianceHistoryValidation,
    ComplianceTempAnswerValidation,

    SaveCcComplianceTempAnswer,
    SaveCcComplianceTempAnswerVersions,
    DeleteCcComplianceTempAnswers,
    DeleteCcComplianceTempAnswerVersions,
    CreateCcComplianceHistory,
    CreateCcComplianceHistoryVersions,
    UpdateCcComplianceState,
    LoadCcComplianceItemState,
    LoadCcComplianceHistory,
    LoadCcComplianceRevisionData,
    LoadCcComplianceTempAnswerList,
    LoadCcComplianceTempAnswers,
    LoadCcComplianceTempAnswerVersions,
    LoadDriveVersionsByIds,

    setCcComplianceListLoadState,
    setCcComplianceItemHistory,
    setCcComplianceItemState,
    setCcComplianceTempAnswerList,
    setCcComplianceTempAnswers,
    setCcComplianceTempAnswerVersions,
    getFile,
    createFileName,
    addModal,
    closeModal,
} from "client/store"
import {
    Delimiter,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    Empty,
    UserIcon,

    SelectDropdown,
    SelectDropdownItem,
    ButtonGroup,
    ButtonGroupItem,

    ListWrapper,
    ListItem,
    ListItemTitle,
    DeleteListItemButton,
    TextButton,

    Button,
    SpinnerIcon,
    CrossIcon,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,

    RecentVersions,
    SetRecentVersions,
    CheckRecentVersions,
} from "client/components"

import Drive from "client/pages/drive"

const RETURN = "RETURN"
const SUBMIT = "SUBMIT"

interface EditComplianceHistoryProps {
    history: CcComplianceHistoryCreate
    historyVersions: MtDriveVersion[]
}

let isMounted: boolean

const Form = ({
    history,
    historyVersions,
}: EditComplianceHistoryProps) => {
    const baseRoute = useSelector((state: StoreType) => state.ccCompliance.baseRoute)
    const revisionActiveList = useSelector((state: StoreType) => state.ccCompliance.revisionActiveList)
    const itemDetails = useSelector((state: StoreType) => state.ccCompliance.itemDetails)
    const itemState = useSelector((state: StoreType) => state.ccCompliance.itemState)

    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[1] || []

    const groupUserList = useSelector((state: StoreType) => state.projectUserGroup.groupUserList)
    const userList = useSelector((state: StoreType) => state.user.userList)

    const responseList = useSelector((state: StoreType) => state.ccResponse.responseList)
    const moduleResponseList = useSelector((state: StoreType) => state.ccResponse.moduleResponseList)
    const responseIds = moduleResponseList[1]
    const responsibleResponseIds = moduleResponseList[0]

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [remark, setRemark] = useInput(history.remark)

    const [answer, setAnswer] = useState(history.answer)
    const [responseId, setResponseId] = useState(history.responseId)
    const [nextUserId, setNextUserId] = useState(history.nextUserId)
    const [versions, setVersions] = useState<MtDriveVersion[]>(historyVersions)

    const [confirmType, setConfirmType] = useState<typeof RETURN | typeof SUBMIT>(null)
    const [showConfirm, setShowConfirm] = useState(false)

    const [validation, setValidation] = useState<ComplianceHistoryValidation | ComplianceTempAnswerValidation>(null)
    const [validationVersions, setValidationVersions] = useState<string>(null)
    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [confirmState, setConfirmState] = useState<StateType>(IDLE)

    const revision = revisionActiveList[itemDetails.librarySetId]

    const isFirstLevel = history.prevLevelId === levelIds[0]
    const firstLevel = levelIds[0] && levelList[levelIds[0]]

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const checkVersions = () => {
        const versionIds: number[] = []
        versions.forEach((version) => {
            versionIds.push(version.id)
        })

        return LoadDriveVersionsByIds(versionIds).then((versionsLoaded) => {
            if (versions.length === versionsLoaded.length) {
                setValidationVersions(null)
                return CheckRecentVersions(project.id).then(() => true)
            }

            const versionIdsLoaded: number[] = []
            versionsLoaded.forEach((version) => {
                versionIdsLoaded.push(version.id)
            })

            setVersions(versions.filter((row) => versionIdsLoaded.includes(row.id)))
            setValidationVersions(lang.validationVersions)

            return CheckRecentVersions(project.id).then(() => false)
        })
    }

    const onSave = () => {
        setSaveState(PENDING)
        checkVersions().then((checkRes) => {
            if (!checkRes) {
                setSaveState(IDLE)
                return
            }

            const tempAnswer: CcComplianceTempAnswer = {
                itemId: history.itemId,
                complianceRevisionId: history.complianceRevisionId,
                editorId: userData.userId,
                responseId,
                answer,
                remark,
                nextUserId,
            }
            SaveCcComplianceTempAnswer(tempAnswer, lang).then((res) => {
                if (res) { // validation not empty
                    if (isMounted) {
                        setValidation(res)
                        setSaveState(IDLE)
                    }
                } else {
                    if (isMounted) setValidation(res)

                    const tempVersions: CcComplianceTempAnswerVersion[] = []
                    versions.forEach((version) => {
                        tempVersions.push({
                            itemId: history.itemId,
                            complianceRevisionId: history.complianceRevisionId,
                            editorId: userData.userId,
                            versionId: version.id,
                        })
                    })

                    DeleteCcComplianceTempAnswerVersions(history.itemId, history.complianceRevisionId, userData.userId).then(() => {
                        SaveCcComplianceTempAnswerVersions(tempVersions).then(() => {
                            // reload temp answer list
                            LoadCcComplianceTempAnswerList(revision.id).then((newTempAnswerList) => {
                                dispatch(setCcComplianceTempAnswerList(revision.setId, newTempAnswerList))
                                if (isMounted) setSaveState(IDLE)
                            })
                        })
                    })
                }
            })
        })
    }

    const onConfirm = () => {
        setConfirmState(PENDING)
        checkVersions().then((checkRes) => {
            if (!checkRes) {
                setConfirmState(IDLE)
                setShowConfirm(false)
                return
            }

            const settings: CcStateSettingsType = itemState && itemState.settings ? JSON.parse(itemState.settings) : {}

            let newHistory: CcComplianceHistoryCreate = {
                ...history,
                nextUserId,
                responseId,
                answer,
                remark,
            }

            if (isFirstLevel) {
                newHistory = {
                    ...newHistory,
                    workflow: WORKFLOW_SEND_TO_LEVEL,
                }
            }

            if (!isFirstLevel) {
                // return to first level
                if (confirmType === RETURN) {
                    newHistory = {
                        ...newHistory,
                        nextLevelId: levelIds[0] || null,
                        nextUserId: (levelIds[0] && settings[levelIds[0]]) || null,
                        nextStatus: OPEN,
                        workflow: WORKFLOW_RETURN_TO_LEVEL,
                    }
                }

                if (confirmType === SUBMIT) {
                    if (newHistory.nextLevelId === null) { // the last approval level
                        newHistory = {
                            ...newHistory,
                            nextStatus: APPROVED,
                            workflow: WORKFLOW_APPROVED,
                        }
                    } else { // send to next level
                        newHistory = {
                            ...newHistory,
                            nextStatus: VERIFICATION,
                            workflow: WORKFLOW_SEND_TO_LEVEL,
                        }
                    }
                }
            }

            CreateCcComplianceHistory(newHistory, lang)
            .then((historyRes) => {
                setValidation(historyRes.validation)

                if (historyRes.newHistoryId) {
                    const newHistoryVersions: CcComplianceHistoryVersion[] = []
                    versions.forEach((version) => {
                        newHistoryVersions.push({
                            complianceHistoryId: historyRes.newHistoryId,
                            versionId: version.id,
                        })
                    })

                    settings[newHistory.nextLevelId] = newHistory.nextUserId

                    const newState: CcComplianceItemState = {
                        itemId: newHistory.itemId,
                        complianceRevisionId: newHistory.complianceRevisionId,
                        levelId: newHistory.nextLevelId,
                        verifierId: newHistory.nextUserId,
                        settings: JSON.stringify(settings),
                    }

                    Promise.all([
                        CreateCcComplianceHistoryVersions(newHistoryVersions),
                        UpdateCcComplianceState(newState),
                        DeleteCcComplianceTempAnswers(itemDetails.id, revision.id),
                    ]).then(() => {
                        // form will be rerendered automatically
                        LoadCcComplianceItemState(itemDetails.id, revision.id).then((res) => {
                            dispatch(setCcComplianceItemState(res))
                        })
                        LoadCcComplianceHistory(itemDetails.id, revision.id).then((res) => {
                            dispatch(setCcComplianceItemHistory(res))
                        })
                        LoadCcComplianceTempAnswers(itemDetails.id, revision.id).then((res) => {
                            dispatch(setCcComplianceTempAnswers(res))
                        })
                        LoadCcComplianceTempAnswerVersions(itemDetails.id, revision.id).then((res) => {
                            dispatch(setCcComplianceTempAnswerVersions(res))
                        })

                        // reload revision data
                        dispatch(setCcComplianceListLoadState(PENDING))
                        LoadCcComplianceRevisionData(revision, dispatch).then(() => {
                            dispatch(setCcComplianceListLoadState(IDLE))
                        })
                    })
                } else if (isMounted) {
                    setConfirmState(IDLE)
                    setShowConfirm(false)
                }
            })
        })
    }

    const openDriveModal = () => {
        dispatch(addModal({
            key: "drive",
            content: <Drive
                onSelectVersion={(newVersion, newFile) => selectVersion(newVersion, newFile)}
                onSelectFiles={(newFiles) => selectFiles(newFiles)}
            />,
        }))
    }

    const selectVersion = (newVersion: MtDriveVersion, newFile: MtDriveFile) => {
        if (!newVersion) return
        const selectedVersion: MtDriveVersion = {
            ...newVersion,
            file: newFile,
        }
        SetRecentVersions(project.id, [selectedVersion])

        const newVersions: MtDriveVersion[] = []
        versions.forEach((row) => {
            if (row.id !== newVersion.id) newVersions.push(row)
        })
        newVersions.push(selectedVersion)
        setVersions(newVersions)
    }

    const selectFiles = (newFiles: readonly MtDriveFile[]) => {
        const selectedVersions: MtDriveVersion[] = []
        const selectedVersionIds: number[] = []
        newFiles.forEach((file) => {
            if (!file.versions || !file.versions.length) return
            const version = file.versions[0]
            selectedVersions.push({
                ...version,
                file,
            })
            selectedVersionIds.push(version.id)
        })
        SetRecentVersions(project.id, selectedVersions)

        let newVersions: MtDriveVersion[] = []
        versions.forEach((row) => {
            if (!selectedVersionIds.includes(row.id)) newVersions.push(row)
        })
        newVersions = newVersions.concat(selectedVersions)
        setVersions(newVersions)
    }

    const deleteVersion = (version: MtDriveVersion) => {
        setVersions(versions.filter((row) => row.id !== version.id))
    }

    const closeConfirm = () => {
        setShowConfirm(false)
    }

    const onCancel = () => {
        replaceHistoryState(baseRoute)
        dispatch(closeModal())
    }

    const options: string[] = []
    if (itemDetails.options.length > 0) {
        const optionsSplited = itemDetails.options.split("\n")
        optionsSplited.forEach((row) => {
            const option = row.trim()
            if (option.length > 0) options.push(option)
        })
    }

    const nextLevel = levelList[history.nextLevelId]
    const nextUser = userList[nextUserId]

    const groupId = nextLevel ? nextLevel.groupId : null
    const userIds = (groupUserList && groupUserList[groupId]) || null

    let disableSave = false
    let disableReturn = false

    // first level user didn't provide answer/response or remark
    if (isFirstLevel) {
        if (
            (answer.trim().length === 0 && !responseId)
            || (itemDetails.answerType !== "text" && remark.trim().length === 0)
        ) {
            disableSave = true
        }
    }

    // other level user didn't provide response or remark
    if (!isFirstLevel) {
        if (responseId === null || remark.trim().length === 0) {
            disableSave = true
            disableReturn = true
        }
    }

    return (
        <Row>
            <Col xs={12}>
                <Delimiter />
                {
                    isFirstLevel && (
                        <FormGroup>
                            <Label>{lang.answer}</Label>
                            {
                                itemDetails.answerType === "text"
                                && <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} />
                            }
                            {
                                itemDetails.answerType === "yes_no" && (
                                    <ButtonGroup>
                                        {
                                            responsibleResponseIds && responsibleResponseIds.map((id) => (
                                                <ButtonGroupItem
                                                    key={id}
                                                    active={responseId === id}
                                                    onClick={() => setResponseId(id)}
                                                >
                                                    {responseList[id] ? responseList[id].response : ""}
                                                </ButtonGroupItem>
                                            ))
                                        }
                                    </ButtonGroup>
                                )
                            }
                            {
                                itemDetails.answerType === "multiple" && (
                                    <ButtonGroup>
                                        {
                                            options.map((option) => (
                                                <ButtonGroupItem
                                                    key={option}
                                                    active={answer === option}
                                                    onClick={() => setAnswer(option)}
                                                >
                                                    {option}
                                                </ButtonGroupItem>
                                            ))
                                        }
                                    </ButtonGroup>
                                )
                            }
                            <Feedback>{validation && validation.answer}</Feedback>
                        </FormGroup>
                    )
                }
                {
                    !isFirstLevel && (
                        <FormGroup>
                            <Label>{lang.response}</Label>
                            <ButtonGroup>
                                {
                                    responseIds.map((id) => (
                                        <ButtonGroupItem
                                            key={id}
                                            active={responseId === id}
                                            onClick={() => setResponseId(id)}
                                        >
                                            {responseList[id] ? responseList[id].response : ""}
                                        </ButtonGroupItem>
                                    ))
                                }
                            </ButtonGroup>
                            <Feedback>{validation && validation.responseId}</Feedback>
                        </FormGroup>
                    )
                }

                <FormGroup>
                    <Label>{lang.remark}</Label>
                    <Textarea value={remark} onChange={setRemark} />
                    <Feedback>{validation && validation.remark}</Feedback>
                </FormGroup>

                <FormGroup>
                    <Label>{lang.files}</Label>
                    <ListWrapper style={{ width: "50%" }}>
                        {
                            versions.map((version) => (
                                <ListItem key={version.id}>
                                    <ListItemTitle>
                                        <a href={getFile(version.file, version).download} target="_blank" rel="noreferrer">
                                            {`${createFileName(version.file)} - ${lang.revision} ${version.revision}`}
                                        </a>
                                    </ListItemTitle>
                                    <DeleteListItemButton onClick={() => deleteVersion(version)}>
                                        <CrossIcon />
                                    </DeleteListItemButton>
                                </ListItem>
                            ))
                        }
                        {
                            !versions.length
                            && <Empty />
                        }
                        <FormGroup inline style={{ marginTop: "5px" }}>
                            <TextButton onClick={openDriveModal}>{`+ ${lang.add}`}</TextButton>
                            <RecentVersions onSelect={(row) => selectVersion(row, row.file)} />
                        </FormGroup>
                        <FormGroup>
                            <Feedback>{validationVersions}</Feedback>
                        </FormGroup>
                    </ListWrapper>
                </FormGroup>

                {
                    // nextLevel is null on the last approval level
                    nextLevel && (
                        <FormGroup>
                            <Label>{lang.approvalLevel}</Label>
                            <Input type="text" value={nextLevel.name} readOnly />
                        </FormGroup>
                    )
                }
                {
                    // users are empty on the last approval level
                    userIds && (
                        <FormGroup>
                            <Label>{lang.assignTo}</Label>
                            <SelectDropdown title={nextUser ? <UserIcon user={nextUser} size="sm" /> : lang.selectUser}>
                                {
                                    userIds.map((userId) => (
                                        <SelectDropdownItem
                                            key={userId}
                                            size="sm"
                                            onClick={() => setNextUserId(userId)}
                                        >
                                            <UserIcon user={userList[userId]} size="sm" />
                                        </SelectDropdownItem>
                                    ))
                                }
                            </SelectDropdown>
                        </FormGroup>
                    )
                }
            </Col>
            <Col xs={12}>
                <Button row variant="success" onClick={onSave}>
                    {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                </Button>
                <Button
                    row
                    variant="success"
                    disabled={disableSave}
                    onClick={() => {
                        setConfirmType(SUBMIT)
                        setShowConfirm(true)
                    }}
                >
                    {lang.submit}
                </Button>
                {
                    !isFirstLevel && (
                        <Button
                            row
                            variant="danger"
                            disabled={disableReturn}
                            onClick={() => {
                                setConfirmType(RETURN)
                                setShowConfirm(true)
                            }}
                        >
                            {lang.returnToLevel.replace("{level}", firstLevel ? firstLevel.name : "")}
                        </Button>
                    )
                }
                <Button row onClick={onCancel}>{lang.cancel}</Button>
            </Col>

            <ConfirmWrapper
                show={showConfirm}
                onClose={closeConfirm}
            >
                <Confirm>
                    <ConfirmTitle>
                        {confirmType === RETURN && lang.returnToLevel.replace("{level}", firstLevel ? firstLevel.name : "")}
                        {confirmType === SUBMIT && lang.submit}
                    </ConfirmTitle>
                    <ConfirmDescription>{lang.confirmCompliance}</ConfirmDescription>

                    <ConfirmBottom>
                        <Button row onClick={closeConfirm}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirm}>
                            {confirmState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </Row>
    )
}
export default Form
